import { GET_DATA } from '@components/Modal/SingleProfileModal/QuerySingleProfile'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { LP_UNIT_TYPE } from '@utils/constants/learningPath'
import find from 'lodash/find'
import get from 'lodash/get'
import map from 'lodash/map'
import some from 'lodash/some'
import orderBy from 'lodash/orderBy'
import React from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'
import { MaterialWithUnits } from '@interfaces/Material'
import { SessionInfoList } from '@interfaces/LessonGroups'
import { GroupMaterial, GroupNote } from '@interfaces/MySchedule'
import { GET_LEARNING_PATH, GET_LESSON_GROUP_DETAIL } from '@queries/index'
import { getRegFromUsers } from '@utils/helpers/lessons'
import { RegistrationMaterial, RegistrationWrapper } from '@interfaces/Student'
import { GET_LEARNING_PATH_BY_LPIDS_ALIAS } from '@queries/learningPath'

interface QueryPageResults {
  loading: boolean
  data: any
  profile: any
  materialOptions: MaterialWithUnits[]
  activeMaterial: string
  refetchData: (LPID?: string) => void
}

interface QueryLessonDetailProp {
  InstructorId: string
  StartDateTime: string
  EndDateTime: string
  SessionIds: string[]
  Language: string
  ProgramId: string
  ProgramType: string
  StudentGroupId: string
  SessionInfoList: SessionInfoList[]
  GroupMaterials?: GroupMaterial[]
  GroupNotes?: GroupNote[]
  isFlex?: boolean
  OnlineLessonURL?: string
  LastMaterialList: Material[]
  children: (results: QueryPageResults) => {}
}

type Props = WithApolloClient<QueryLessonDetailProp & WithUserTimeProps>

class QueryPageData extends React.PureComponent<Props> {
  state = {
    loading: true,
    data: undefined,
    profile: undefined,
    groupNotes: [] as GroupNote[],
    materialOptions: [] as MaterialWithUnits[],
    registrations: [] as RegistrationWrapper[],
    activeMaterial: '',
  }

  async componentDidMount() {
    this.fetchData()
  }

  fetchData = async (LPID?: string) => {
    this.setState({ loading: true })
    const { ProgramId, LastMaterialList } = this.props
    try {
      const LastMaterial = LastMaterialList

      const result = await this.getGroupDetail()
      result.GroupClassList = this.concatUsersNote(result.GroupClassList)
      const profile = await this.getProfile()
      this.setState({ profile })
      const regMats = this.state.registrations
        .filter((reg) => reg.RegistrationInfo.Programs.map((prog) => prog.ProgramId).indexOf(ProgramId) >= 0)
        .reduce((mats: RegistrationMaterial[], reg: RegistrationWrapper) => {
          return mats.concat(reg.RegistrationInfo?.Materials)
        }, [])
      const matsFromReg = regMats.map(({ LPID }) => LPID)
      //const materialOptions = await this.getLearningPath(this.props.SessionInfoList, regMats)
      const materialOptions = await this.getLearningPath2(
        this.props.SessionInfoList,
        regMats,
        LPID ?? LastMaterial?.[0]?.Material
      )

      const uniqMaterials = ([] as MaterialWithUnits[]).concat
        .apply([], materialOptions)
        .reduce((arr: MaterialWithUnits[], c: MaterialWithUnits) => {
          const exitingMats = arr.map((m) => m?.Id)
          if (exitingMats.indexOf(c?.Id) < 0) {
            arr.push(c)
          }
          return arr
        }, [])
      const mOptions: MaterialWithUnits[] = uniqMaterials.filter((mat) => matsFromReg.indexOf(mat?.Id) >= 0)

      this.setState({
        data: result.GroupClassList[0],
        loading: false,
        materialOptions: mOptions,
        /**
         * an active material clicked by the user
         */
        activeMaterial: LPID,
      })
    } catch (error) {
      this.setState({ loading: false })
    }
  }

  getGroupDetail = async (sessionIds = [] as string[]) => {
    const { client, InstructorId, StartDateTime, EndDateTime, ProgramId, Language = '' } = this.props
    const SessionIds = sessionIds.length > 1 ? sessionIds.join(',') : this.props.SessionIds.join(',')

    const params = {
      InstructorId,
      StartDateTime,
      EndDateTime,
      FILTER_TYPE: 'Details',
      ProgramId,
      SessionIds,
      Language,
    }

    const res = await client.query({
      query: GET_LESSON_GROUP_DETAIL,
      variables: { params },
      fetchPolicy: 'network-only',
    })

    return get(res.data, 'getLessonGroups.0.InstructorGroupInfo', {})
  }

  getLearningPath = async (siList: SessionInfoList[], regMaterials: RegistrationMaterial[]) => {
    const { client, profile, isFlex } = this.props
    const promisedMaterialOptions = map(siList, async (session) => {
      const res = await client.query({
        query: GET_LEARNING_PATH,
        variables: {
          StudentId: session.CustomerId,
          IdType: profile.Type,
          isFlex,
          berlitzEnv: session.MyBerlitzEnvironment,
        },
        fetchPolicy: 'network-only',
      })

      const data: any = get(res, 'data')
      let options = []
      if (data && data.getLearningPath) {
        const lp = data.getLearningPath.map((learning) => {
          /**
           * https://berlitz.atlassian.net/browse/PTL-552
           */
          const unitsInOrdered = orderBy(get(learning.details, 'units', []), ['order'])

          let units = unitsInOrdered
          const unit = units.find((val) => val.fullname.includes('Unit 0 -'))
          if (unit) {
            units = units.filter((u) => u.unitid !== get(unit, 'unitid'))
          }

          if (isFlex) {
            units = units.filter((unit) => unit['unittype'] === LP_UNIT_TYPE.COACHING)
          }

          units = map(units, (val) => {
            let url =
              get(find(val.activities, { type: 'ig' }), 'url') || get(find(val?.attachments, { type: 'ig_pdf' }), 'url')
            if (val.unittype === LP_UNIT_TYPE.COACHING) {
              url = get(find(val?.activities, { type: 'p5_instructor' }), 'url')
            }

            return {
              Id: val.unitid,
              Name: val.fullname,
              URL: url,
              UnitType: val.unittype,
              Order: val.order,
            }
          })

          return {
            Chapter: '',
            Id: `${learning.lpid}`,
            Name: get(learning, 'shortname', get(learning.details, 'fullname.0.fullname')),
            Page: '',
            Point: '',
            Type: 'LearningPath',
            Units: units,
            IsFlex: some(learning.details?.units, (unit) => unit.unittype === LP_UNIT_TYPE.COACHING),
            IsTest: regMaterials.find(({ LPID }) => LPID === learning.lpid)?.IsTest ?? false,
          }
        })
        options = options.concat(lp)
      }

      return options
    })

    const promisedOptions = Promise.all(promisedMaterialOptions)
    return promisedOptions
  }

  getLearningPath2 = async (
    SessionInfoList: SessionInfoList[],
    regMaterials: RegistrationMaterial[],
    LPID?: string
  ): Promise<MaterialWithUnits[][]> => {
    const { client, profile, isFlex } = this.props
    const promisedMaterialOptions = map(SessionInfoList, async (session) => {
      let options: MaterialWithUnits[] = []

      if (!session.CustomerId) {
        return options
      }

      const res = await client.query({
        query: GET_LEARNING_PATH_BY_LPIDS_ALIAS,
        variables: {
          StudentId: session.CustomerId,
          IdType: profile.Type,
          Ids: LPID || '',
          berlitzEnv: session.MyBerlitzEnvironment,
        },
      })

      const data: any = get(res, 'data')
      const d = regMaterials
        .map((r) => {
          return {
            lpid: r.LPID,
            details: {
              units: [] as any,
            },
            shortname: r.MaterialName,
          }
        })
        .reduce((acc, val) => {
          if (!data || !data.getLearningPath) {
            acc.push(val)
            return acc
          }

          const rm = data.getLearningPath.find((lp) => lp.lpid === val.lpid)

          if (rm) {
            const newVal = {
              ...val,
              details: rm?.details,
            }
            acc.push(newVal)
          } else {
            acc.push(val)
          }

          return acc
        }, [] as any)

      if (d.length > 0) {
        const lp = d.map((learning) => {
          /**
           * https://berlitz.atlassian.net/browse/PTL-552
           */
          const unitsInOrdered = orderBy(get(learning.details, 'units', []), ['order'])

          let units = get(learning.details, 'units', [])
          const unit = units.find((val) => val.unittype === LP_UNIT_TYPE.MATERIAL)
          if (unit) {
            units = units.filter((u) => u.unitid !== get(unit, 'unitid'))
          }

          if (isFlex) {
            units = units.filter((unit) => unit['unittype'] === LP_UNIT_TYPE.COACHING)
          } else {
            units = units.filter((unit) => ![LP_UNIT_TYPE.COACHING].includes(unit['unittype']))
          }

          units = map(units, (val) => {
            /**
             * https://berlitz.atlassian.net/wiki/spaces/B2/pages/3174957080/Instructor+Guides+-+Open+materials
             *
             * Private Student and Groups - Live Online (all type of programs)
             * ig - Instructor Guide HTML guides
             */
            let url =
              get(find(val.activities, { type: 'ig' }), 'url') || get(find(val?.attachments, { type: 'ig_pdf' }), 'url')

            /**
             * Berlitz Flex
             * p5_instructor - Instructor Guide HTML Berlitz Flex guides
             */
            if (val.unittype === LP_UNIT_TYPE.COACHING) {
              url = get(find(val?.activities, { type: 'p5_instructor' }), 'url')
            }

            return {
              Id: val.unitid,
              Name: val.fullname,
              URL: url,
              UnitType: val.unittype,
            }
          })

          return {
            Chapter: '',
            Id: `${learning.lpid}`,
            Name: learning.shortname || get(learning.details, 'fullname.0.fullname'),
            Page: '',
            Point: '',
            Type: 'LearningPath',
            Units: units,
            IsTest: regMaterials.find(({ LPID }) => LPID === learning.lpid)?.IsTest ?? false,
          }
        })
        options = options.concat(lp)
      }
      return options
    })

    return Promise.all(promisedMaterialOptions)
  }

  concatUsersNote = (groups) => {
    return map(groups, (group) => {
      const notes = map(group.StudentNotes, (note, index: number) => ({
        Id: index + 1,
        Name: note.StudentName,
        Role: 'Student',
        Message: note.Note,
        BackgroundColor: 'red100',
        ForegroundColor: 'white',
      }))

      const instructor = {
        Id: notes.length + 1,
        Name: group.InstructorNote.InstructorName,
        Role: 'Instructor',
        Message: group.InstructorNote.Note,
        BackgroundColor: 'purple100',
        ForegroundColor: 'white',
      }

      notes.push(instructor)

      group.Notes = notes
      return group
    })
  }

  getProfile = async () => {
    const { StudentGroupId, client, Language, ProgramType, SessionInfoList } = this.props

    let StudentIds = [StudentGroupId]
    if (ProgramType !== 'Individual') {
      StudentIds = SessionInfoList?.map((session) => session?.CustomerId)
    }

    const requests = StudentIds.map((StudentId) => {
      const params = {
        StudentId,
        Language,
      }

      return client.query({
        query: GET_DATA,
        variables: params,
        fetchPolicy: 'network-only',
      })
    })
    const responses = await Promise.all(requests)
    let studentProfiles = responses.map((i) => i.data?.getStudents?.[0]?.StudentProfileInfo)
    let registrations = getRegFromUsers(studentProfiles)
    this.setState({ registrations })

    return get(responses[0].data, 'getStudents.0.StudentProfileInfo', {})
  }

  render() {
    const { children } = this.props
    const { loading, data, materialOptions, profile, activeMaterial } = this.state

    return <>{children({ loading, data, materialOptions, profile, activeMaterial, refetchData: this.fetchData })}</>
  }
}

export default WithUserTimeHOC(withApollo(QueryPageData))
